<template>
  <fieldset class="border rounded p-3 mb-3">
    <legend class="float-none w-auto px-2 fs-6">Sinal do agendamento</legend>
    <label class="form-check mb-3">
      <input v-model="form.enabled" class="form-check-input" type="checkbox" @change="publish" />
      <span class="form-check-label">Solicitar sinal pelo WhatsApp</span>
    </label>
    <div v-if="form.enabled" class="row g-3">
      <div class="col-sm-6">
        <label class="form-label">Tipo do sinal</label>
        <select v-model="form.type" aria-label="Tipo do sinal" class="form-select" @change="finishValueEditing">
          <option value="fixed">Valor fixo (R$)</option>
          <option v-if="priceType === 'FIXED'" value="percent">Percentual do serviço (%)</option>
        </select>
      </div>
      <div class="col-sm-6">
        <label class="form-label">{{ form.type === 'percent' ? 'Percentual (%)' : 'Valor do sinal (R$)' }}</label>
        <input ref="valueInput" :value="valueText" :aria-label="form.type === 'percent' ? 'Percentual (%)' : 'Valor do sinal (R$)'" type="text" inputmode="decimal" autocomplete="off" placeholder="Ex.: 50,00" required class="form-control" @input="updateValue" @blur="finishValueEditing" />
      </div>
      <div class="col-sm-6">
        <label class="form-label">Chave Pix padrão do serviço</label>
        <input v-model="form.pix_key" aria-label="Chave Pix padrão do serviço" required maxlength="140" class="form-control" @input="publish" />
      </div>
      <div class="col-sm-6">
        <label class="form-label">Nome do titular</label>
        <input v-model="form.holder" aria-label="Nome do titular" required maxlength="150" class="form-control" @input="publish" />
      </div>
      <div class="col-12">
        <label class="form-check">
          <input v-model="form.cancel_if_unpaid" type="checkbox" class="form-check-input" @change="publish" />
          <span class="form-check-label">Cancelar o agendamento se não houver nenhum valor de sinal identificado no prazo</span>
        </label>
        <small class="text-muted">Com esta opção, a conversa aguarda o comprovante. Se houver sinal parcial, o agendamento será mantido ao vencer o prazo. Desativada, a conversa segue normalmente.</small>
      </div>
      <div v-if="form.cancel_if_unpaid" class="col-sm-6">
        <label class="form-label">Prazo para o sinal (minutos)</label>
        <input v-model.number="form.timeout_minutes" aria-label="Prazo para o sinal (minutos)" type="number" min="1" max="43200" required class="form-control" @input="publish" />
      </div>
      <div class="col-12">
        <label class="form-check">
          <input v-model="differentPix" type="checkbox" class="form-check-input" @change="toggleOverrides" />
          <span class="form-check-label">Usar uma chave diferente por profissional</span>
        </label>
        <div v-if="differentPix" class="mt-3">
          <div v-for="user in selectedUsers" :key="user.id" class="border rounded p-2 mb-2">
            <label class="form-check">
              <input :checked="!!override(user.id)" type="checkbox" class="form-check-input" @change="toggleUser(user.id, $event.target.checked)" />
              <span class="form-check-label">Pix específico para {{ user.name }}</span>
            </label>
            <div v-if="override(user.id)" class="row g-2 mt-1">
              <div class="col-sm-6"><input v-model="override(user.id).pix_key" placeholder="Chave Pix" aria-label="Chave Pix do profissional" required maxlength="140" class="form-control" @input="publish" /></div>
              <div class="col-sm-6"><input v-model="override(user.id).holder" placeholder="Nome do titular" aria-label="Titular do Pix do profissional" required maxlength="150" class="form-control" @input="publish" /></div>
            </div>
          </div>
          <small v-if="!selectedUsers.length" class="text-muted">Selecione os profissionais que prestam este serviço.</small>
        </div>
      </div>
      <div class="col-12"><small class="text-muted">O sinal será reconhecido a partir de imagens ou PDFs de comprovantes, sem confirmação bancária. A configuração é aplicada aos novos agendamentos feitos pelo WhatsApp.</small></div>
    </div>
  </fieldset>
</template>

<script>
const defaults = () => ({ enabled: false, type: 'fixed', value: '35.00', pix_key: '', holder: '', cancel_if_unpaid: false, timeout_minutes: 30, professional_pix: [] });
// Keep the text untouched while typing; only the emitted value uses a decimal point.
const parseValue = value => {
  let text = String(value ?? '').trim().replace(/^R\$\s*/, '').replace(/\s/g, '');
  if (/^\d{1,3}(?:\.\d{3})+(?:,\d{0,2})?$/.test(text)) text = text.replace(/\./g, '');
  else if (/^\d{1,3}(?:,\d{3})+(?:\.\d{0,2})?$/.test(text)) text = text.replace(/,/g, '');
  text = text.replace(',', '.');
  if (/^\.\d{1,2}$/.test(text)) text = `0${text}`;
  if (!/^\d+(?:\.\d{0,2})?$/.test(text)) return null;
  const [whole, fraction = ''] = text.split('.');
  return `${whole.replace(/^0+(?=\d)/, '')}.${fraction.padEnd(2, '0')}`;
};
const formatValue = value => {
  const normalized = parseValue(value);
  return normalized === null ? String(value ?? '') : new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(normalized));
};
export default {
  name: 'DepositConfigurationForm',
  props: { modelValue: { type: Object, default: null }, priceType: { type: String, default: 'FIXED' }, users: { type: Array, default: () => [] }, userIds: { type: Array, default: () => [] } },
  emits: ['update:modelValue'],
  data() { return { form: defaults(), differentPix: false, valueText: null }; },
  computed: { selectedUsers() { return this.users.filter(user => this.userIds.map(String).includes(String(user.id))); } },
  watch: {
    modelValue: { immediate: true, handler(value) {
      const previousValue = this.form.value;
      this.form = { ...defaults(), ...(value || {}), professional_pix: (value?.professional_pix || []).map(pix => ({ ...pix })) };
      if (this.valueText === null || this.form.value !== previousValue) this.valueText = formatValue(this.form.value);
      if (this.form.professional_pix.length > 0) this.differentPix = true;
      this.$nextTick(this.validateValue);
    } },
    priceType(value) { if (value !== 'FIXED' && this.form.type === 'percent') { this.form.type = 'fixed'; this.validateValue(); this.publish(); } },
    userIds() { this.form.professional_pix = this.form.professional_pix.filter(pix => this.userIds.map(String).includes(String(pix.user_id))); this.publish(); },
  },
  methods: {
    updateValue(event) {
      this.valueText = event.target.value;
      this.form.value = parseValue(this.valueText) ?? '';
      this.validateValue();
      this.publish();
    },
    validateValue() {
      const normalized = parseValue(this.valueText);
      const maximum = this.form.type === 'percent' ? 100 : 9999999999.99;
      const valid = normalized !== null && Number(normalized) >= 0.01 && Number(normalized) <= maximum;
      this.$refs.valueInput?.setCustomValidity(valid || this.valueText === '' ? '' : this.form.type === 'percent' ? 'Informe um percentual entre 0,01 e 100.' : 'Informe um valor entre R$ 0,01 e R$ 9.999.999.999,99, com até duas casas decimais.');
    },
    finishValueEditing() {
      this.form.value = parseValue(this.valueText) ?? '';
      this.validateValue();
      if (parseValue(this.valueText) !== null) this.valueText = formatValue(this.form.value);
      this.publish();
    },
    override(id) { return this.form.professional_pix.find(pix => String(pix.user_id) === String(id)); },
    toggleOverrides() { if (!this.differentPix) this.form.professional_pix = []; this.publish(); },
    toggleUser(id, checked) {
      this.form.professional_pix = this.form.professional_pix.filter(pix => String(pix.user_id) !== String(id));
      if (checked) this.form.professional_pix.push({ user_id: Number(id), pix_key: '', holder: '' });
      this.publish();
    },
    publish() { this.$emit('update:modelValue', { ...this.form, value: this.form.enabled ? this.form.value : parseValue(this.form.value) ?? '0.00', professional_pix: this.form.professional_pix.map(pix => ({ ...pix })) }); },
  },
};
</script>
